import {
    getSelectOptionFromValue,
    basisOptions,
    componentOptions,
    operatorMap,
    criteriaOptions,
    milkTypeOptions,
    paymentAmountOptions,
    rateCategoryOptions,
    ruleTypes,
    subcategories,
    whenCondition,
    ConditionOptions,
    mathOperatorToExpressionMap,
    criteriaOptionsMap,
    externalPricesOptionsMap,
    // wrap
} from '../../../../../configured-components/form/configured-inputs/special/payment-line-item-rates/select-options';
import moment from 'moment-timezone';

const roleToNameMap = {
    transport: 'Hauler',
    producer: 'Producer',
    assignee: 'Vendor',
};

export const isEffectiveRateBasic = (effectiveRate) => effectiveRate.payment_rate.length === 1;

// Determine if it's a basic rate - if the payment_rate on the effective rate only has one element
// then it's a basic rate, otherwise it's a tiered (or complex) rate
export const getRateType = (effectiveRate) => {
    const rateType = isEffectiveRateBasic(effectiveRate) ? 'basic' : 'tiered';
    return getSelectOptionFromValue(ruleTypes, rateType);
};

/**
 * Gets the rate values from a program array depending on the basis type.
 * @param {*} program The program array
 * @returns An object with the rate, basis, milkType, component, and modifiers values
 */
const getValuesFromProgram = (program) => {
    const [fn, rate, basis, ...rest] = program;
    const milkTypeAndComponentBases = ['cwt', 'lbs'];
    const hasComponentPounds = ['percent_cwt_milk_value', 'percent_component_milk_value'];

    if (basis === 'fixed') {
        return { rate, basis };
    }

    if (milkTypeAndComponentBases.includes(basis)) {
        const [milkType, component, modifiers] = rest;
        return {
            rate,
            basis,
            milkType,
            component,
            modifiers,
        };
    }
    if (hasComponentPounds.includes(basis)) {
        // Check if the first value in rest is a number (componentPounds) or modifiers
        if (!Number.isNaN(Number(rest[0]))) {
            const [componentPounds] = rest;
            return {
                rate,
                basis,
                componentPounds: parseFloat(componentPounds),
            };
        }
    }
    const [milkType, modifiers] = rest;
    return {
        rate,
        basis,
        milkType,
        modifiers,
    };
};

// Parses a basic program in the format [rate, basis, milkType?, component?]
export const parseBasicProgram = (program, lineItemRates) => {
    if (!program) return null;
    const { rate, basis, milkType, component, modifiers, componentPounds } = getValuesFromProgram(program);
    if (Array.isArray(rate)) {
        // This is a complex rate with mathematical operations
        return {
            rateFormula: parseComplexRate(rate, lineItemRates),
            complexRate: true,
            basis: getSelectOptionFromValue(basisOptions, basis),
            milkType: milkType ? getSelectOptionFromValue(milkTypeOptions, milkType) : null,
            component: component ? getSelectOptionFromValue(componentOptions, component) : null,
            minCharge: modifiers?.min_charge,
            maxCharge: modifiers?.max_charge,
            applyPerPickup: modifiers?.apply_per_pickup,
            componentPounds,
        };
    }
    return {
        rate,
        basis: getSelectOptionFromValue(basisOptions, basis),
        milkType: milkType ? getSelectOptionFromValue(milkTypeOptions, milkType) : null,
        component: component ? getSelectOptionFromValue(componentOptions, component) : null,
        minCharge: modifiers?.min_charge,
        maxCharge: modifiers?.max_charge,
        applyPerPickup: modifiers?.apply_per_pickup,
        componentPounds,
    };
};

/**
 * Parses a complex rate that contains mathematical operations in an array JSON format and returns a string representation to use in the rateFormula field.
 *
 * @param rate Complex rate with mathematical operations in the format ["mul", ["div", ["get", "pounds_shipped"], 100], ["div", ["get", "SCC"], 100]]
 * @returns A string representation of the complex rate in the format @MULTIPLY(@DIVIDE("pounds_shipped", 100), @DIVIDE(@GET("SCC"), 100))
 */
const parseComplexRate = (rate, lineItemRates) => {
    let rateFormula = JSON.stringify(rate);
    Object.entries(mathOperatorToExpressionMap).forEach(([key, value]) => {
        rateFormula = rateFormula.replace(new RegExp(`\\["${value}",`, 'g'), `${key}(`);
    });
    // Handle external prices
    /* eslint-disable-next-line */
    rateFormula = rateFormula.replace(new RegExp(`\\["external_prices_layer",`, 'g'), '@EXTERNAL_PRICE(');
    // Replace external price values with their display keys
    Object.entries(externalPricesOptionsMap).forEach(([key, value]) => {
        rateFormula = rateFormula.replace(new RegExp(`"${value}"`, 'g'), key);
    });
    // We need to replace current payment rate first as it has a different format to get the value of the rate
    /* eslint-disable-next-line */
    rateFormula = rateFormula.replace(new RegExp(`\\["rate_value",\\["get","payment_rate"\\]\\]`, 'g'), '#CurrentPaymentRate');
    Object.entries(criteriaOptionsMap).forEach(([key, value]) => {
        rateFormula = rateFormula.replace(new RegExp(`\\["get","${value}"\\]`, 'g'), key);
    });
    lineItemRates.forEach((lineItemRate) => {
        if (rateFormula.includes(lineItemRate._id.toString())) {
            const executeRateExpr = `\\["rate_value",\\["execute_rate","${lineItemRate._id}"\\]\\]`;
            const rateIdentifier = `@EXECUTE_RATE(${lineItemRate.unique_id})`;
            rateFormula = rateFormula.replace(new RegExp(executeRateExpr, 'g'), rateIdentifier);
        }
    });
    rateFormula = rateFormula.replace(/\]/g, ')');
    rateFormula = rateFormula.replace(/,/g, ', ');
    return rateFormula;
};

function parseConditionExpression(expr) {
    const [operator, ...operands] = expr;

    if (operator === 'all' || operator === 'any') {
        let conditions = [];

        /* eslint-disable-next-line */
        for (const operand of operands) {
            // Recursively process nested 'all' or 'any'
            const nestedConditions = parseConditionExpression(operand);
            conditions = conditions.concat(nestedConditions);
        }

        // Within the current 'all' block, combine 'gte' and 'lte' on the same criteria into 'between'
        const combinedConditions = [];
        const conditionsByCriteria = {};
        /* eslint-disable-next-line */
        for (const condition of conditions) {
            const criteriaValue = condition.criteria;
            if (!conditionsByCriteria[criteriaValue]) {
                conditionsByCriteria[criteriaValue] = [];
            }
            conditionsByCriteria[criteriaValue].push(condition);
        }

        /* eslint-disable-next-line */
        for (const criteriaValue in conditionsByCriteria) {
            const conditionsForCriteria = conditionsByCriteria[criteriaValue];
            const gteCondition = conditionsForCriteria.find((c) => c.condition === ConditionOptions.GREATER_THAN_OR_EQUAL_TO);
            const lteCondition = conditionsForCriteria.find((c) => c.condition === ConditionOptions.LESS_THAN_OR_EQUAL_TO);
            if (gteCondition && lteCondition && conditionsForCriteria.length === 2) {
                // Combine into 'between' condition
                combinedConditions.push({
                    criteria: gteCondition.criteria,
                    condition: operatorMap.between,
                    amount: gteCondition.amount,
                    amountUpper: lteCondition.amount,
                });
            } else {
                combinedConditions.push(...conditionsForCriteria);
            }
        }

        return combinedConditions;
        // eslint-disable-next-line no-else-return
    } else if (operator === 'gte' || operator === 'lte' || operator === 'eq' || operator === 'lt' || operator === 'gt') {
        const [left, right] = operands;
        const amount = right.toString();
        const operatorMapping = operatorMap[operator];
        if (left[0] === 'get') {
            let criteriaVal = left[1];
            const criteria = getSelectOptionFromValue(criteriaOptions, criteriaVal);
            return [
                {
                    criteria,
                    condition: operatorMapping,
                    amount,
                },
            ];
        }
        if (left[0] === 'rate_value') {
            let criteriaVal = 'payment_rate';
            const criteria = getSelectOptionFromValue(criteriaOptions, criteriaVal);
            return [
                {
                    criteria,
                    condition: operatorMapping,
                    amount,
                },
            ];
        }
        throw new Error('Unsupported left operand in condition');
    } else {
        throw new Error(`Unsupported operator: ${operator}`);
    }
}

function parseComplexProgram(program, lineItemRates) {
    const desiredArray = [];

    let index = 0;

    while (index < program.length) {
        if (program[index][0] === 'set' && program[index][1] === 'payment_rate' && Array.isArray(program[index][2]) && JSON.stringify(program[index][2]) === JSON.stringify(['as_array_literal', 0, 'fixed'])) {
            index += 1;
        }

        const ifExpressionArray = program[index][2];
        if (!ifExpressionArray || ifExpressionArray[0] !== 'if') {
            // Reached the end of the conditions
            break;
        }

        const [, conditionExpr, rateExpr, elseExpr] = ifExpressionArray;
        const rate = parseBasicProgram(rateExpr, lineItemRates);
        const rateObject = {
            when: getSelectOptionFromValue(whenCondition, conditionExpr[0]),
            conditions: [],
            ...rate,
        };

        const conditions = parseConditionExpression(conditionExpr);
        rateObject.conditions = conditions;
        desiredArray.push(rateObject);
        index += 1;
    }

    return desiredArray;
}

/**
 * Parse the payer or payee filter model property into something the Filter component can work with
 * The model object would look something like this:
 * { role: string, ids: string[], user_group: string[] }
 * And the filter object needs something like this:
 * {
 *  payee_filter: { role: string, ids: string[] },
 *  payee_filter_filters: [{ filterType: { name: string, id: string } }],
 * }
 * where the filterType is a select option for the first dropdown filter which could look like this:
 * [{ label: 'All', value: 'all' }, { label: 'Individual Producers', value: 'ids' }]
 */
const getPayFilter = (filter, userGroups, filterName) => {
    if (!filter) {
        return {};
    }
    const roleName = roleToNameMap[filter?.role];
    const updates = {
        [filterName]: { role: filter?.role },
        [`${filterName}_filters`]: [],
    };
    if (filter.ids?.length > 0) {
        updates[filterName] = { ids: filter.ids };
        updates[`${filterName}_filters`] = [
            {
                filterType: { name: `Individual ${roleName}s`, id: 'ids' },
            },
        ];
    }
    if (filter.user_group?.length > 0) {
        updates[filterName] = { ...updates[filterName], user_group: filter.user_group };
        updates[`${filterName}_filters`] = [
            ...updates[`${filterName}_filters`],
            {
                filterType: { name: `${roleName} Groups`, id: 'user_group' },
            },
        ];
    }
    if (filter.milk_category?.length > 0) {
        updates[filterName] = { ...updates[filterName], milk_category: filter.milk_category };
        updates[`${filterName}_filters`] = [
            ...updates[`${filterName}_filters`],
            {
                filterType: { name: 'Member Type', id: 'milk_category' },
            },
        ];
    }
    if (filter.pay_group?.length > 0) {
        updates[filterName] = { ...updates[filterName], pay_group: filter.pay_group };
        updates[`${filterName}_filters`] = [
            ...updates[`${filterName}_filters`],
            {
                filterType: { name: 'Pay Zone', id: 'pay_group' },
            },
        ];
    }
    if (filter.federal_order?.length > 0) {
        updates[filterName] = { ...updates[filterName], federal_order: filter.federal_order };
        updates[`${filterName}_filters`] = [
            ...updates[`${filterName}_filters`],
            {
                filterType: { name: 'Federal Order', id: 'federal_order' },
            },
        ];
    }
    if (filter.state?.length > 0) {
        updates[filterName] = { ...updates[filterName], state: filter.state };
        updates[`${filterName}_filters`] = [
            ...updates[`${filterName}_filters`],
            {
                filterType: { name: 'State', id: 'state' },
            },
        ];
    }
    // If no filters were set, default to 'All'
    if (updates[`${filterName}_filters`].length === 0) {
        updates[`${filterName}_filters`] = [
            {
                filterType: { name: 'All', id: 'all' },
            },
        ];
    }
    return updates;
};

const getPaymentFrequencyValue = (paymentFrequency) => {
    const hasSM = paymentFrequency?.includes('SM');
    const hasMO = paymentFrequency?.includes('MO');
    if (hasSM && hasMO) {
        return 'SM_MO';
    }
    return hasSM ? 'SM' : hasMO ? 'MO' : null;
};

const getRuleType = (config) => {
    const payeeRole = config.payee_filter.role;
    const payerRole = config.payer_filter.role;
    if (payeeRole === 'transport' && payerRole === 'producer') {
        return 'producerHauler';
    }
    if (payeeRole === 'assignee' && payerRole === 'producer') {
        return 'producerVendor';
    }
    if (payeeRole === 'assignee' && payerRole === 'transport') {
        return 'haulerVendor';
    }
    throw new Error(`Unsupported rule type: ${payeeRole} - ${payerRole}`);
};

export function translateEffectiveRate(effectiveRate, lineItemRates) {
    let basicPaymentRate = {};
    let rules = [];
    if (isEffectiveRateBasic(effectiveRate)) {
        basicPaymentRate = parseBasicProgram(effectiveRate.payment_rate[0], lineItemRates);
    } else {
        rules = parseComplexProgram(effectiveRate.payment_rate, lineItemRates);
    }
    return {
        basicPaymentRate,
        rules,
    };
}

export default function translateLineItemConfigToFormObject(source, lineItemRates) {
    let desired = {
        ruleType: getRuleType(source),
        name: source.name,
        description: source.description,
        priority: source.priority,
        payment_frequency: getPaymentFrequencyValue(source.payment_frequency),
        defer_advance_payment: source.defer_advance_payment,
        start_date: source.start_date,
        end_date: source.end_date,
        contract_min_charge_per_period: source.contract_min_charge_per_period,
        contract_max_charge_per_period: source.contract_max_charge_per_period,
    };
    desired.rates = source.payment_rate?.map((rate) => {
        let [currentEffectiveRate] = rate.effective_rates.sort((a, b) => moment(b.effective_date).diff(moment(a.effective_date))).filter(({ effective_date }) => moment(effective_date).isAfter(moment()));
        if (currentEffectiveRate == null) {
            [currentEffectiveRate] = rate.effective_rates;
        }
        return paymentLineItemRateToForm(rate, currentEffectiveRate, lineItemRates);
    });

    desired = {
        ...desired,
        ...getPayFilter(source.payee_filter, source.payee_user_group, 'payee_filter'),
        ...getPayFilter(source.payee_exclusion_filter, source.payee_user_group, 'payee_exclusion_filter'),
        ...getPayFilter(source.payer_filter, source.payer_user_group, 'payer_filter'),
        ...getPayFilter(source.payer_exclusion_filter, source.payer_user_group, 'payer_exclusion_filter'),
    };

    return desired;
}

export const paymentLineItemRateToForm = (rate, effectiveRate, lineItemRates) => {
    const { basicPaymentRate, rules } = translateEffectiveRate(effectiveRate, lineItemRates);
    return {
        _id: rate._id,
        subcategory: getSelectOptionFromValue(subcategories, rate.subcategory),
        rate_name: rate.rate_name,
        description: rate.description,
        rulesType: getRateType(effectiveRate),
        paid_by_payer: rate.paid_by_payer,
        paid_to_payee: getSelectOptionFromValue(paymentAmountOptions, rate.paid_to_payee),
        payee_category: getSelectOptionFromValue(rateCategoryOptions, rate.payee_category),
        payer_category: getSelectOptionFromValue(rateCategoryOptions, rate.payer_category),
        rate: basicPaymentRate,
        basis_parameter_filter: effectiveRate.basis_parameter_filter,
        average_override: effectiveRate.average_override,
        line_item_group: rate.line_item_group,
        gl_number: rate.gl_number,
        account: rate.account,
        reference: rate.reference,
        hide_zero_dollar_line_item: rate.hide_zero_dollar_line_item,
        linked_payment_line_item_rate_id: rate.linked_payment_line_item_rate_id,
        linked_payment_line_item_rate_reason: rate.linked_payment_line_item_rate_reason,
        rules,
        unique_id: rate.unique_id,
    };
};
